import type {
  ActionSetDto,
  AutoTransactionRuleDto,
  ConditionalActionMatcherDto,
  FieldName,
  PromptSpec,
} from "../../api/types";
import type { StoredTransaction } from "../../logic/derivedAccount";
import { expandActions, renderBuiltins } from "../../logic/action-set-template";

/**
 * One "ask user" action, found wherever it lives.
 *
 * Prompts used to be read straight off `rule.actions`, which meant a prompt
 * inside a cited action set was never asked for and silently applied nothing.
 * Expanding first fixes that, and brings two consequences worth naming:
 * prompts are identified by position rather than by field (one rule can now
 * legitimately ask for the description twice), and a prompt that expansion
 * left under a guard has to have the user's answer put back under the same
 * guard, or it would apply unconditionally.
 */
export type CollectedPrompt = {
  spec: PromptSpec;
  wrap: ConditionalActionMatcherDto | null;
};

export const collectPrompts = (
  rule: AutoTransactionRuleDto | null,
  sets: readonly ActionSetDto[] | undefined,
): CollectedPrompt[] => {
  if (!rule) return [];
  return expandActions(rule.actions, sets).flatMap<CollectedPrompt>(
    (action) => {
      if (action.field === "prompt")
        return [{ spec: action.value, wrap: null }];
      if (
        action.field === "conditional" &&
        action.value.action.field === "prompt"
      ) {
        return [
          { spec: action.value.action.value, wrap: action.value.condition },
        ];
      }
      return [];
    },
  );
};

/**
 * The transaction's fields as text, for a `{$…}` in a prompt prefix.
 *
 * Entity fields are already names here rather than ids, so there is nothing to
 * resolve. This reads the transaction's *current* values, where the backend
 * would read the state as of that action's position in the rule — the two can
 * differ if an earlier action in the same rule wrote the field. That is
 * acceptable for a prefix, which is only a seed the user then edits; it is not
 * acceptable anywhere the value is written straight through, and nothing here
 * does that.
 */
const transactionField = (
  transaction: StoredTransaction,
  field: FieldName,
): string => {
  switch (field) {
    case "source":
      return transaction.source;
    case "merchant":
      return transaction.merchant;
    case "description":
      return transaction.description;
    case "group":
      return transaction.group;
    case "amount":
      return transaction.amount.amount;
    case "expenseType":
      return transaction.expenseType;
    case "datePaid":
      return transaction.datePaid;
    case "kid":
      return transaction.kid ?? "";
    case "payToAccount":
      return transaction.payToAccount ?? "";
  }
};

/** A prefix is template source too; an unresolvable one is shown as written. */
export const renderPrefix = (
  prefix: string,
  transaction: StoredTransaction,
): string =>
  renderBuiltins(prefix, (field) => transactionField(transaction, field)) ??
  prefix;

/**
 * Labels for a list of prompts, disambiguated when a rule asks for the same
 * field more than once — which a cited set makes possible for the first time.
 */
export const promptLabels = (
  prompts: CollectedPrompt[],
  labelOf: (field: FieldName) => string,
): string[] => {
  const seen = new Map<FieldName, number>();
  const totals = new Map<FieldName, number>();
  for (const p of prompts) {
    totals.set(p.spec.field, (totals.get(p.spec.field) ?? 0) + 1);
  }
  return prompts.map((p) => {
    const label = labelOf(p.spec.field);
    if ((totals.get(p.spec.field) ?? 0) < 2) return label;
    const n = (seen.get(p.spec.field) ?? 0) + 1;
    seen.set(p.spec.field, n);
    return `${label} (${n})`;
  });
};
